import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, updateDoc, setDoc } from "firebase/firestore";
import { storage, db } from "../../../firebase";
import { getSummaryKey } from "./EditTools";
import { default_object } from "../../Custom/projects/data";
import { logHistory } from "../HistoryDrawer";

// Helper to look up original/default images for a project
export const getProjectDefaultImages = (projectId) => {
  if (!projectId || !default_object) return [];
  const cleanId = String(projectId).trim().toLowerCase();
  const projects = default_object?.root?.LWongWeb?.PROJECTS || {};
  for (const catKey of Object.keys(projects)) {
    const cat = projects[catKey];
    if (cat && typeof cat === "object") {
      for (const projKey of Object.keys(cat)) {
        const item = cat[projKey];
        if (
          projKey.trim().toLowerCase() === cleanId ||
          (item?.name && String(item.name).trim().toLowerCase() === cleanId) ||
          (item?.id && String(item.id).trim().toLowerCase() === cleanId)
        ) {
          return Array.isArray(item?.images) ? item.images : [];
        }
      }
    }
  }
  return [];
};

function ImageManager(props) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItems, setModalItems] = useState([]);
  const [imgErrors, setImgErrors] = useState({});

  const fallbackImages = getProjectDefaultImages(props.id);

  // Normalize raw images to clean string array and resolve empty/missing slots
  const rawList = Array.isArray(props.images)
    ? props.images
    : typeof props.images === "string" && props.images.trim() !== ""
    ? [props.images]
    : [];

  const rawDisabled = Array.isArray(props.disabledImages)
    ? props.disabledImages
    : [];

  // Resolve URLs: if empty string or invalid, fallback to data.js definition
  let hadHealedEmpty = false;
  let resolvedList = (rawList.length > 0 ? rawList : fallbackImages).map((item, idx) => {
    let url = typeof item === "string" ? item.trim() : item?.src || item?.url || "";
    if (!url && fallbackImages[idx]) {
      url = fallbackImages[idx];
      hadHealedEmpty = true;
    }
    return url;
  }).filter(Boolean);

  // Auto-heal empty items in Firestore if we repaired empty strings
  useEffect(() => {
    if (hadHealedEmpty && props.path && props.id && resolvedList.length > 0) {
      try {
        const docRef = doc(db, props.path, props.id);
        updateDoc(docRef, { images: resolvedList });

        const summaryKey = getSummaryKey(props.path, props.id);
        const summaryRef = doc(db, "trees", "users", "bhzfBvdgxZYrvKvDaXz5iaQzIjn1", "summary");
        setDoc(summaryRef, { [summaryKey]: { images: resolvedList } }, { merge: true });
        if (props.onImagesChanged) props.onImagesChanged(resolvedList);
      } catch (e) {
        console.warn("Auto-heal images warning:", e);
      }
    }
  }, [hadHealedEmpty, props.path, props.id]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    setUploadProgress(0);

    const newUrls = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const storagePath = `trees/images/${Date.now()}_${file.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const storageRef = ref(storage, storagePath);
        const uploadTask = uploadBytesResumable(storageRef, file);

        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress = Math.round(
                ((i + snapshot.bytesTransferred / snapshot.totalBytes) / files.length) * 100
              );
              setUploadProgress(progress);
            },
            (error) => reject(error),
            async () => {
              const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref);
              newUrls.push(downloadUrl);
              resolve();
            }
          );
        });
      }

      const updatedList = [...resolvedList, ...newUrls];
      if (props.path && props.id) {
        const docRef = doc(db, props.path, props.id);
        await updateDoc(docRef, { images: updatedList });

        const summaryKey = getSummaryKey(props.path, props.id);
        const summaryRef = doc(db, "trees", "users", "bhzfBvdgxZYrvKvDaXz5iaQzIjn1", "summary");
        await setDoc(summaryRef, { [summaryKey]: { images: updatedList } }, { merge: true });

        await logHistory({
          branchName: props.id,
          path: props.path,
          docId: props.id,
          action: `Uploaded ${newUrls.length} image(s)`,
          previousData: { images: resolvedList },
          newData: { images: updatedList },
          changedKeys: ["images"],
        });

        if (props.onImagesChanged) props.onImagesChanged(updatedList);
      }
    } catch (err) {
      console.error("Error uploading image:", err);
      alert("Error uploading image: " + err.message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = "";
    }
  };

  const openConfigureModal = () => {
    // Build items with active status
    const activeItems = resolvedList.map((url, idx) => ({
      id: `${url}-${idx}`,
      url,
      active: true,
    }));
    const disabledItems = rawDisabled.map((url, idx) => ({
      id: `disabled-${url}-${idx}`,
      url,
      active: false,
    }));
    setModalItems([...activeItems, ...disabledItems]);
    setModalOpen(true);
  };

  const moveModalItem = (index, targetIndex) => {
    if (targetIndex < 0 || targetIndex >= modalItems.length) return;
    const nextItems = [...modalItems];
    const [moved] = nextItems.splice(index, 1);
    nextItems.splice(targetIndex, 0, moved);
    setModalItems(nextItems);
  };

  const toggleModalItemActive = (index) => {
    const nextItems = [...modalItems];
    nextItems[index] = {
      ...nextItems[index],
      active: !nextItems[index].active,
    };
    setModalItems(nextItems);
  };

  const toggleAll = (activeState) => {
    setModalItems((prev) => prev.map((item) => ({ ...item, active: activeState })));
  };

  const saveModalChanges = async () => {
    const activeUrls = modalItems.filter((it) => it.active).map((it) => it.url);
    const disabledUrls = modalItems.filter((it) => !it.active).map((it) => it.url);

    if (props.path && props.id) {
      try {
        const docRef = doc(db, props.path, props.id);
        await updateDoc(docRef, {
          images: activeUrls,
          disabledImages: disabledUrls,
        });

        const summaryKey = getSummaryKey(props.path, props.id);
        const summaryRef = doc(db, "trees", "users", "bhzfBvdgxZYrvKvDaXz5iaQzIjn1", "summary");
        await setDoc(
          summaryRef,
          {
            [summaryKey]: {
              images: activeUrls,
              disabledImages: disabledUrls,
            },
          },
          { merge: true }
        );

        await logHistory({
          branchName: props.id,
          path: props.path,
          docId: props.id,
          action: "Reconfigured images (reordered / toggled)",
          previousData: { images: resolvedList, disabledImages: rawDisabled },
          newData: { images: activeUrls, disabledImages: disabledUrls },
          changedKeys: ["images", "disabledImages"],
        });

        if (props.onImagesChanged) {
          props.onImagesChanged(activeUrls);
        }
      } catch (err) {
        console.error("Error saving image configuration:", err);
        alert("Error saving image order: " + err.message);
        return;
      }
    }
    setModalOpen(false);
  };

  const getImageSrc = (url, index) => {
    if (imgErrors[index]) {
      if (fallbackImages[index] && fallbackImages[index] !== url) {
        return fallbackImages[index];
      }
      return "";
    }
    return url || (fallbackImages[index] ? fallbackImages[index] : "");
  };

  const handleImgError = (index) => {
    setImgErrors((prev) => ({ ...prev, [index]: true }));
  };

  return (
    <Container>
      <Header>
        <Title>Images ({resolvedList.length})</Title>
        <HeaderActions>
          <ConfigureBtn
            type="button"
            onClick={openConfigureModal}
            title="Configure image order and visibility"
          >
            ⚙ Configure
          </ConfigureBtn>
          <UploadLabel>
            {uploading ? `Uploading ${uploadProgress}%` : "+ Add Image"}
            <HiddenInput
              type="file"
              accept="image/*"
              multiple
              disabled={uploading}
              onChange={handleFileUpload}
            />
          </UploadLabel>
        </HeaderActions>
      </Header>

      {uploading && (
        <ProgressBar>
          <ProgressFill width={uploadProgress} />
        </ProgressBar>
      )}

      <Gallery>
        {resolvedList.length === 0 && !uploading && (
          <EmptyMsg>No images yet. Click "+ Add Image" to upload.</EmptyMsg>
        )}
        {resolvedList.map((url, index) => {
          const src = getImageSrc(url, index);
          return (
            <ThumbCard
              key={`${url}-${index}`}
              title={`Image #${index + 1}`}
              style={
                src
                  ? {
                      backgroundImage: `url("${src}")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      backgroundRepeat: "no-repeat",
                    }
                  : undefined
              }
            >
              <IndexBadge>#{index + 1}</IndexBadge>
              {!src ? (
                <PlaceholderThumb>
                  <span>🖼️</span>
                  <span>#{index + 1}</span>
                </PlaceholderThumb>
              ) : (
                <HiddenPreloadImg
                  src={src}
                  referrerPolicy="no-referrer"
                  alt=""
                  onError={() => handleImgError(index)}
                />
              )}
            </ThumbCard>
          );
        })}
      </Gallery>

      {/* Configure Modal */}
      {modalOpen && (
        <ModalBackdrop onClick={() => setModalOpen(false)}>
          <ModalDialog onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>Configure Project Images</ModalTitle>
                <ModalSubtitle>
                  {props.projectName || props.id || "Reorder and toggle visibility"}
                </ModalSubtitle>
              </div>
              <ModalCloseBtn onClick={() => setModalOpen(false)}>✕</ModalCloseBtn>
            </ModalHeader>

            <ModalToolbar>
              <ActiveCounter>
                {modalItems.filter((x) => x.active).length} of {modalItems.length} active
              </ActiveCounter>
              <ToolbarBtns>
                <SmallBtn type="button" onClick={() => toggleAll(true)}>
                  Enable All
                </SmallBtn>
                <SmallBtn type="button" onClick={() => toggleAll(false)}>
                  Disable All
                </SmallBtn>
              </ToolbarBtns>
            </ModalToolbar>

            <ModalBody>
              {modalItems.length === 0 ? (
                <EmptyMsg style={{ padding: "2rem" }}>No images to configure.</EmptyMsg>
              ) : (
                modalItems.map((item, idx) => {
                  const itemSrc = getImageSrc(item.url, idx);
                  return (
                    <ItemRow key={item.id} $active={item.active}>
                      <ItemOrderBadge>#{idx + 1}</ItemOrderBadge>
                      <ItemThumbContainer>
                        {itemSrc ? (
                          <>
                            <ItemThumb
                              style={{
                                backgroundImage: `url("${itemSrc}")`,
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                backgroundRepeat: "no-repeat",
                              }}
                            />
                            <HiddenPreloadImg
                              src={itemSrc}
                              referrerPolicy="no-referrer"
                              alt=""
                              onError={() => handleImgError(idx)}
                            />
                          </>
                        ) : (
                          <PlaceholderThumb style={{ fontSize: "9px" }}>
                            <span>🖼️</span>
                            <span>#{idx + 1}</span>
                          </PlaceholderThumb>
                        )}
                      </ItemThumbContainer>

                      <ItemDetails>
                        <ItemStatusBadge $active={item.active}>
                          {item.active ? "● Active on site" : "○ Hidden from site"}
                        </ItemStatusBadge>
                        <ItemUrlPreview title={item.url}>
                          {decodeURIComponent(item.url.split("?")[0].split("/").pop() || item.url)}
                        </ItemUrlPreview>
                      </ItemDetails>

                    <ItemControls>
                      <ArrowBtn
                        type="button"
                        disabled={idx === 0}
                        onClick={() => moveModalItem(idx, idx - 1)}
                        title="Move Up"
                      >
                        ▲
                      </ArrowBtn>
                      <ArrowBtn
                        type="button"
                        disabled={idx === modalItems.length - 1}
                        onClick={() => moveModalItem(idx, idx + 1)}
                        title="Move Down"
                      >
                        ▼
                      </ArrowBtn>

                      <ToggleBtn
                        type="button"
                        $active={item.active}
                        onClick={() => toggleModalItemActive(idx)}
                        title={item.active ? "Click to hide image" : "Click to show image"}
                      >
                        {item.active ? "✓ On" : "✕ Off"}
                      </ToggleBtn>
                    </ItemControls>
                  </ItemRow>
                  );
                })
              )}
            </ModalBody>

            <ModalFooter>
              <CancelBtn type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </CancelBtn>
              <SaveBtn type="button" onClick={saveModalChanges}>
                Save Changes
              </SaveBtn>
            </ModalFooter>
          </ModalDialog>
        </ModalBackdrop>
      )}
    </Container>
  );
}

export default ImageManager;

const Container = styled.div`
  margin-top: 10px;
  background-color: #f7f9fa;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e1e4e8;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const Title = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #1c1c1e;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConfigureBtn = styled.button`
  background-color: #34495e;
  color: white;
  padding: 6px 11px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: #2c3e50;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const UploadLabel = styled.label`
  background-color: #2a9d8e;
  color: white;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s, box-shadow 0.2s;
  user-select: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    background-color: #238276;
    box-shadow: 0 2px 8px rgba(42, 157, 142, 0.35);
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 8px;
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: #2a9d8e;
  width: ${({ width }) => width}%;
  transition: width 0.2s ease;
`;

const Gallery = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 0;
`;

const EmptyMsg = styled.div`
  font-size: 12px;
  color: #888;
  padding: 8px 0;
  text-align: center;
  width: 100%;
`;

const ThumbCard = styled.div`
  position: relative;
  width: 65px;
  height: 65px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #ddd;
  background-color: #eaeded;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const IndexBadge = styled.div`
  position: absolute;
  top: 3px;
  left: 3px;
  background: rgba(0, 0, 0, 0.75);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 4px;
  border-radius: 3px;
  line-height: 13px;
  pointer-events: none;
  z-index: 2;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.4);
`;

const HiddenPreloadImg = styled.img`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`;

const PlaceholderThumb = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #e9ecef;
  color: #6c757d;
  font-size: 11px;
  font-weight: 600;
`;

/* Modal Styles */
const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  backdrop-filter: blur(3px);
`;

const ModalDialog = styled.div`
  background: #ffffff;
  width: 92%;
  max-width: 620px;
  max-height: 85vh;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  animation: popIn 0.2s ease-out;

  @keyframes popIn {
    from {
      opacity: 0;
      transform: scale(0.96);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #e1e4e8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f8fafc;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
`;

const ModalSubtitle = styled.p`
  margin: 3px 0 0 0;
  font-size: 12px;
  color: #64748b;
`;

const ModalCloseBtn = styled.button`
  background: transparent;
  border: none;
  font-size: 18px;
  color: #64748b;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background 0.15s;

  &:hover {
    background: #e2e8f0;
    color: #0f172a;
  }
`;

const ModalToolbar = styled.div`
  padding: 10px 20px;
  background: #f1f5f9;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActiveCounter = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #334155;
`;

const ToolbarBtns = styled.div`
  display: flex;
  gap: 6px;
`;

const SmallBtn = styled.button`
  background: #ffffff;
  border: 1px solid #cbd5e1;
  color: #334155;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
    border-color: #94a3b8;
  }
`;

const ModalBody = styled.div`
  padding: 12px 20px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? "#e2e8f0" : "#f1f5f9")};
  background-color: ${({ $active }) => ($active ? "#ffffff" : "#f8fafc")};
  opacity: ${({ $active }) => ($active ? "1" : "0.55")};
  transition: background-color 0.2s, opacity 0.2s;

  &:hover {
    background-color: ${({ $active }) => ($active ? "#f8fafc" : "#f1f5f9")};
  }
`;

const ItemOrderBadge = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  width: 28px;
  text-align: center;
  flex-shrink: 0;
`;

const ItemThumbContainer = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #cbd5e1;
  background-color: #eaeded;
  flex-shrink: 0;
`;

const ItemThumb = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const ItemDetails = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ItemStatusBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? "#059669" : "#64748b")};
`;

const ItemUrlPreview = styled.div`
  font-size: 11px;
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemControls = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
`;

const ArrowBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #1e293b;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    border-color: #94a3b8;
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

const ToggleBtn = styled.button`
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  background-color: ${({ $active }) => ($active ? "#e0f2fe" : "#f1f5f9")};
  color: ${({ $active }) => ($active ? "#0369a1" : "#64748b")};
  border: 1px solid ${({ $active }) => ($active ? "#bae6fd" : "#cbd5e1")};
  transition: all 0.15s;

  &:hover {
    background-color: ${({ $active }) => ($active ? "#bae6fd" : "#e2e8f0")};
  }
`;

const ModalFooter = styled.div`
  padding: 14px 20px;
  border-top: 1px solid #e1e4e8;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  background: #f8fafc;
`;

const CancelBtn = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
`;

const SaveBtn = styled.button`
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  background: #2a9d8e;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #238276;
  }
`;
